<template>
  <Page :fill="false">
    <!-- Header -->
    <div class="bg-background-card rounded-lg shadow-card p-6">
      <div class="flex items-center gap-4">
        <div class="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center shrink-0">
          <i class="fa-solid fa-diagram-project text-lg" />
        </div>
        <div>
          <h1 class="text-2xl font-bold text-primary">Import from DataX</h1>
          <p class="text-sm text-muted">Bring data extracted by DataX into ADAM Cloud.</p>
        </div>
      </div>

      <!-- Integration picker -->
      <div class="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4" role="radiogroup" aria-label="DataX integration">
        <button
          v-for="option in integrations"
          :key="option.id"
          type="button"
          role="radio"
          :aria-checked="integration === option.id"
          class="integration-card"
          :class="{ 'integration-card--active': integration === option.id }"
          @click="selectIntegration(option.id)"
        >
          <i :class="['fa-solid', option.icon, 'text-lg mt-0.5']" aria-hidden="true" />
          <span class="min-w-0 text-left">
            <span class="block font-semibold">{{ option.label }}</span>
            <span class="block text-xs text-muted">{{ option.description }}</span>
          </span>
        </button>
      </div>
    </div>

    <!-- Each integration keeps its progress when the user switches away and back. -->
    <KeepAlive>
      <DataXHierarchyImport v-if="integration === 'hierarchy'" />
      <DataXBomImport v-else-if="integration === 'bom'" />
      <DataXSpecsImport v-else-if="integration === 'specs'" />
      <DataXPmImport v-else />
    </KeepAlive>
  </Page>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';
import DataXBomImport from '@/features/load-sheet/components/DataXBomImport.vue';
import DataXHierarchyImport from '@/features/load-sheet/components/DataXHierarchyImport.vue';
import DataXPmImport from '@/features/load-sheet/components/DataXPmImport.vue';
import DataXSpecsImport from '@/features/load-sheet/components/DataXSpecsImport.vue';

definePage({ meta: { requiresProject: true } });

type DataXIntegration = 'hierarchy' | 'bom' | 'specs' | 'pm';

const integrations: { id: DataXIntegration; label: string; description: string; icon: string }[] = [
  {
    id: 'hierarchy',
    label: 'Asset Hierarchy',
    description: 'Turn a P&ID hierarchy extraction into Asset load sheet rows.',
    icon: 'fa-sitemap',
  },
  {
    id: 'bom',
    label: 'BoM Extraction',
    description: 'Create Materials and Bills of Materials from a DataX bill of materials.',
    icon: 'fa-boxes-stacked',
  },
  {
    id: 'specs',
    label: 'Asset Specifications',
    description: 'Append DataX asset specifications to the Notes of existing assets.',
    icon: 'fa-clipboard-list',
  },
  {
    id: 'pm',
    label: 'OEM Maintenance Analysis',
    description: 'Create an OEM maintenance analysis per asset from a DataX PM schedule.',
    icon: 'fa-screwdriver-wrench',
  },
];

const route = useRoute();
const router = useRouter();

/** Kept in the query string so a link can open straight to one integration. */
const integration = computed<DataXIntegration>(() => {
  const requested = route.query.integration;
  return integrations.some((option) => option.id === requested) ? (requested as DataXIntegration) : 'hierarchy';
});

function selectIntegration(id: DataXIntegration): void {
  if (id === integration.value) return;
  router.replace({ query: { ...route.query, integration: id } });
}
</script>

<style scoped>
@reference '../../../style.css';
.integration-card {
  @apply flex items-start gap-3 rounded-lg border border-border p-3 text-text transition-colors;
  @apply hover:bg-primary/5 focus:outline-hidden focus:ring-2 focus:ring-primary/40;
}

.integration-card--active {
  @apply border-primary bg-primary/10 text-primary;
}
</style>
